import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnChanges,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {MatDatepicker} from '@angular/material/datepicker';
import {DateAdapter} from '@angular/material/core';
import {TranslateService} from '@ngx-translate/core';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {DateFieldConfig, DatePickerModeEnum} from '../date-field.config';
import {MydocmaDateAdapter} from "@mydocma/component-library/src/lib/form";

class ModeAwareDateAdapter extends MydocmaDateAdapter {
    private _mode: DatePickerModeEnum = DatePickerModeEnum.date;

    setMode(mode: DatePickerModeEnum): void {
        this._mode = mode ?? DatePickerModeEnum.date;
    }

    override format(date: Date, displayFormat: any): string {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }

        switch (this._mode) {
            case DatePickerModeEnum.month:
                return super.format(date, `MM${this.getSeparator()}yyyy`);

            case DatePickerModeEnum.year:
                return super.format(date, 'yyyy')

            case DatePickerModeEnum.date:
            default:
                return super.format(date, displayFormat)
        }
    }

    private getSeparator(): string {
        return this.locale?.startsWith('en') ? '/' : '.';
    }
}

@Component({
    selector: 'mydocma-form-date-field',
    templateUrl: './date-field.component.html',
    styleUrls: ['./date-field.component.scss'],
    providers: [
        {provide: DateAdapter, useClass: ModeAwareDateAdapter},
    ],
})
export class DateFieldComponent
    extends BaseFieldComponent<DateFieldConfig>
    implements AfterViewInit, OnDestroy, OnChanges {

    @ViewChild('dateInput', {static: false}) inputEl!: ElementRef<HTMLInputElement>;
    @ViewChild('picker', {static: false}) picker!: MatDatepicker<Date>;

    public dateFilterListener!: (d: Date) => boolean;
    public readonly staticCssClass = 'mydocma-date-field';
    public dateClassRef = this.dateClass.bind(this);
    DatePickerModeEnum = DatePickerModeEnum;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        private readonly adapter: DateAdapter<Date>,
        private readonly translateService: TranslateService,
        element: ElementRef,
        renderer: Renderer2
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, element, renderer);
        this.dateFilterListener = this.myFilter;
    }

    get mode(): DatePickerModeEnum {
        return this.fieldWrapper?.config?.mode ?? DatePickerModeEnum.date;
    }

    ngAfterViewInit(): void {
        const modeAwareAdapter = this.adapter as ModeAwareDateAdapter;

        modeAwareAdapter.setMode(this.mode);
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['fieldWrapper']) {
            (this.adapter as ModeAwareDateAdapter).setMode(this.mode);
            const v = this.control?.value;
            Promise.resolve().then(() => this.control?.setValue(v, {emitEvent: false}));
        }
    }

    ngOnDestroy(): void {
    }


    public onClick() {
        if (!this.fieldWrapper.config.readonly && !this.fieldWrapper.config.disabled) {
            this.picker.open();
        }
        super.onControlClick();
    }

    public onBlur() {
        this.eventDispatcher.notifyControlBlur(this.fieldWrapper.config);
    }


    public myFilter = (d: Date): boolean => {
        if (this.mode !== DatePickerModeEnum.date) return true;
        const fn = (this.fieldWrapper?.config as DateFieldConfig)?.dateFilter;
        return fn ? fn(d) : true;
    };


    public dateClass(cellDate: { getDate: () => any }, view: string): string | undefined {
        if (this.fieldWrapper.config?.getDateClass && view === 'month') {
            return this.fieldWrapper.config.getDateClass(cellDate as unknown as Date, view);
        }
        return undefined;
    }

    clearDateField() {
        this.control.setValue(null);
        this.control.markAsTouched();
    }


    public onLeftArrowClick() {
        if ((this.fieldWrapper.config as DateFieldConfig).leftArrowClick) {
            (this.fieldWrapper.config as DateFieldConfig).leftArrowClick();
            return;
        }
        this.stepDate(-1);
    }

    public onRightArrowClick() {
        if ((this.fieldWrapper.config as DateFieldConfig).rightArrowClick) {
            (this.fieldWrapper.config as DateFieldConfig).rightArrowClick();
            return;
        }
        this.stepDate(1);
    }

    private startOfDay(d: Date): Date {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x;
    }

    private stepDate(step: number) {
        if (this.fieldWrapper.config.readonly || this.fieldWrapper.config.disabled) return;

        const ctrl = this.formGroup.get(this.fieldWrapper.config.name);
        const raw = ctrl?.value;
        let next = raw instanceof Date ? new Date(raw) : new Date(raw ?? Date.now());
        if (isNaN(next.getTime())) next = new Date();
        next.setHours(0, 0, 0, 0);

        switch (this.mode) {
            case DatePickerModeEnum.month:
                next.setMonth(next.getMonth() + step, 1);
                break;
            case DatePickerModeEnum.year:
                next.setFullYear(next.getFullYear() + step, 1, 1);
                break;
            default:
                next.setDate(next.getDate() + step);
        }
        next.setHours(0, 0, 0, 0);

        const {minDate, maxDate} = this.fieldWrapper.config;
        if (minDate && next < this.startOfDay(minDate)) next = this.startOfDay(minDate);
        if (maxDate && next > this.startOfDay(maxDate)) next = this.startOfDay(maxDate);

        ctrl?.setValue(next);
    }


    chosenMonthHandler(date: Date, dp: MatDatepicker<Date>) {
        const value = new Date(date.getFullYear(), date.getMonth(), 1);
        value.setHours(0, 0, 0, 0);
        this.control.setValue(value);
        this.control.markAsTouched();
        dp.close();
    }

    chosenYearHandler(date: Date, dp: MatDatepicker<Date>) {
        const value = new Date(date.getFullYear(), 0, 1);
        value.setHours(0, 0, 0, 0);
        this.control.setValue(value);
        this.control.markAsTouched();
        dp.close();
    }
}
